import { NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseService, FileFolderType, UploadedFile, WebviewCommunicationService } from '@shared/core';
import { LocalizationPipe } from '@shared/modules/localization';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { UIIcon } from '../../../icon';
import { IFormFieldFileConfig } from '../../models';
import { BaseFormFieldComponent, baseProviderComponent } from '../base-form-field.component';

@Component({
  selector: 'ui-form-file',
  templateUrl: './form-file.component.html',
  styleUrl: './form-file.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    NzFormModule,
    NzInputModule,
    UIIcon,
    FormsModule,
    LocalizationPipe,
  ],
  providers: [...baseProviderComponent(UIFormFile)],
})
export class UIFormFile extends BaseFormFieldComponent<string | File | UploadedFile | undefined> implements OnInit {
  @ViewChild('formFileInput', { static: true }) formFileInput?: ElementRef;

  @Input() override set config(configValue: IFormFieldFileConfig) {
    this.label = configValue.label;
    this.placeholder = configValue.placeholder ?? '';
    this.hideLabel = configValue.hideLabel ?? false;
    this.folderType = configValue.folderType;
  }

  folderType?: FileFolderType;

  get fileName(): string {
    if (this.value instanceof File) {
      return this.value.name;
    }
    if (this.isUploadedFile(this.value)) {
      return this.value.fileName;
    }
    return this.value || '';
  }

  private baseService = inject(BaseService);
  private cdr = inject(ChangeDetectorRef);
  private webviewService = inject(WebviewCommunicationService);

  isFileDownloading = false;
  isFileUploading = false;

  ngOnInit(): void {
    this.webviewService.onFileUploaded((data: UploadedFile) => {
      if (data.fieldId === this.fieldName && !this.value && !this.disabled) {
        this.value = data;
        this.onChange(this.value);
        this.updateControlValidity();
        this.isFileUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onFileInputClicked(): void {
    if (this.webviewService.isNsWebViewInterfaceInitialized$.getValue() && this.fieldName) {
      this.isFileUploading = true;
      this.webviewService.requestFileUpload(this.fieldName);
    } else {
      this.formFileInput?.nativeElement?.click();
    }
  }

  private isUploadedFile(value: unknown): value is UploadedFile {
    return typeof value === 'object' && value !== null && 'file' in value && 'fileName' in value;
  }

  onFileSelected(event: Event): void {
    if (this.isFileUploading) {
      return;
    }
    if (this.value || this.disabled) {
      return;
    }
    this.isFileUploading = true;
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      const file = input.files[0];

      this.value = file;
      this.onChange(this.value);
      this.updateControlValidity();
      this.isFileUploading = false;
      input.value = '';
    }
  }

  onDownload(event: Event): void {
    event.stopPropagation();
    if (!this.value || this.isFileDownloading) {
      return;
    }

    if (this.value instanceof File) {
      this.baseService.downloadStaticFile(this.value);
      return;
    }

    if (typeof this.value === 'string') {
      this.isFileDownloading = true;
      this.baseService.downloadFile(
        this.value,
        () => {
          this.isFileDownloading = false;
          this.cdr.detectChanges();
        },
        () => {
          this.isFileDownloading = false;
          this.cdr.detectChanges();
        },
      );
    }
  }

  onRemoveFile(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.value = undefined;
    this.onChange(this.value);
    this.updateControlValidity();
  }
}
